import { warn } from '@execonline-inc/logging';
import { InvalidUrlError, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { BadStatus } from 'ajaxian';
import { Task } from 'taskarian';
import { AppyError, callApi } from '../../../../../../Appy';
import { currentUserStore } from '../../../../../../CurrentUserStore';
import ErrorActionableReaction, {
  EAProps,
  handleError,
} from '../../../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../../../LinkyLinky';
import { participantResourceDecoder } from '../../../../../../ParticipantsStore/Decoders';
import { Link } from '../../../../../../Resource/Types';
import { TPlainTextKey } from '../../../../../../Translations';
import { toWallClockString } from '../../../../../../Utils/timezone';
import { groupParticipantsResourceDecoder } from '../../../../../People/Group/GroupParticipantsStore/Decoders';
import { LearningCollectionResource } from '../../../../Types';
import {
  participantsInvitedResourceDecoder,
  productLicenseInformationResourceDecoder,
} from '../../../Edit/CurrentCollectionStep/Share/PublishSection/Share/InviteModalAndShareUrl/InviteParticipantModal/Store/Decoder';
import {
  allSelectedParticipantIds,
  buildLicenseInformationUrl,
  filterGroups,
  filterParticipants,
} from '../../../Functions';
import InviteParticipantStore from './Store';
import { State } from './Types';

interface Props extends EAProps<InviteParticipantStore> {
  learningCollectionResource: LearningCollectionResource;
}

type InviteError = AppyError | InvalidUrlError | MissingLinkError;

const badStatusErrorMessage = (error: BadStatus): TPlainTextKey => {
  switch (error.response.body) {
    case 'Validation failed: User is already enrolled in this course':
      return 'User already enrolled';
    case 'Validation failed: User has already been taken':
      return 'User already invited';
    case 'All participants must belong to the same org when bulk license provisioning':
      return 'All participants must belong to the same org when bulk license provisioning';
    case 'Send email date must be in the future':
      return 'Send email date must be in the future';
    default:
      return 'We are unable to process your request at this time';
  }
};

const handleInviteError = (store: InviteParticipantStore) => (error: InviteError) => {
  switch (error.kind) {
    case 'bad-status':
      store.error(badStatusErrorMessage(error));
      break;
    case 'missing-link-error':
    case 'invalid-url-error':
      store.error('We are unable to process your request at this time');
      warn('InviteParticipantsReactions', error.kind, error);
      break;
    default:
      handleError(store, error);
  }
};

const sendParticipantRequest = callApi(participantResourceDecoder, {});

const sendGroupRequest = callApi(groupParticipantsResourceDecoder, {});

const buildUrl = (link: Link): Task<InviteError, Link> =>
  toUrlT(link.href).map((url): Link => ({ ...link, href: url.href }));

const getParticipant = (link: Link) => buildUrl(link).andThen(sendParticipantRequest);

const getGroup = (link: Link) => buildUrl(link).andThen(sendGroupRequest);

const learningCollectionInvitationsEncoder = (
  time: Date,
  timezone: string,
  date: Date,
  skipCommunications: boolean,
  resource: LearningCollectionResource,
  userIds: number[],
  groupIds: number[],
  programIds: number[],
) => ({
  use_case_id: resource.payload.id,
  user_ids: userIds,
  group_ids: groupIds,
  program_ids: programIds,
  direct_enrollment: true,
  // Send the typed wall-clock plus the selected zone and let the server
  // convert. The previous toLocaleString -> new Date round-trip re-parsed a
  // zone-rendered string as browser-local, shifting the time by the gap
  // between the two zones — an Eastern admin scheduling 9am Pacific stored
  // 3am Pacific (DVLP-7797).
  send_email_date: toWallClockString(
    new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes(),
    ),
  ),
  timezone,
  skip_communications: skipCommunications,
});

const processLink =
  (id: string, replace: string) =>
  (link: Link): Task<InviteError, Link> =>
    toUrlT(link.href.replace(`{${replace}}`, `${id}`)).map(
      (url): Link => ({ ...link, href: url.href }),
    );

class InviteParticipantReactions extends ErrorActionableReaction<
  InviteParticipantStore,
  State,
  Props
> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store, learningCollectionResource } = this.props;
    switch (state.kind) {
      case 'new-participant':
        Task.succeed<InviteError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('participant'))
          .andThen(processLink(state.participantId, 'participant_id'))
          .andThen(getParticipant)
          .fork(handleInviteError(store), store.getLicenseInformationWithNewParticipant);
        break;
      case 'new-group':
        Task.succeed<InviteError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('user-group'))
          .andThen(processLink(state.groupId, 'group_id'))
          .andThen(getGroup)
          .fork(handleInviteError(store), store.getLicenseInformationWithNewGroup);
        break;
      case 'remove-participant':
        store.licenseInformationWithRemovedParticipant();
        break;
      case 'remove-group':
        store.licenseInformationWithRemovedGroup();
        break;
      case 'submitting':
        Task.succeed<InviteError, ReadonlyArray<Link>>(learningCollectionResource.links)
          .andThen(findLinkT('invite'))
          .andThen(
            callApi(
              participantsInvitedResourceDecoder,
              learningCollectionInvitationsEncoder(
                state.time,
                state.timezone,
                state.date,
                state.skipCommunications,
                learningCollectionResource,
                filterParticipants(state.newGroupsAndParticipants).map((p) => p.payload.id),
                filterGroups(state.newGroupsAndParticipants).map((p) => p.payload.id),
                state.programIds,
              ),
            ),
          )
          .fork(handleInviteError(store), (participantsInvitedResource) => {
            store.participantsInvited(state.shareUrl, participantsInvitedResource);
          });
        break;
      case 'get-license-information':
        Task.succeed<InviteError, ReadonlyArray<Link>>(learningCollectionResource.links)
          .andThen(findLinkT('product-licenses'))
          .andThen(
            buildLicenseInformationUrl(allSelectedParticipantIds(state.newGroupsAndParticipants)),
          )
          .andThen(callApi(productLicenseInformationResourceDecoder, {}))
          .fork(handleInviteError(store), store.readyWithLicenseInformation);
        break;
      case 'ready':
      case 'inviting':
      case 'participants-invited':
      case 'selecting-date':
      case 'error':
      case 'waiting':
      case 'download-program':
        break;
      default:
        assertNever(state);
    }
  };
}

export default InviteParticipantReactions;
